export const LEGACY_IMPORT_COLUMNS = [
  'source_system',
  'source_reference',
  'customer_full_name',
  'customer_first_name',
  'customer_last_name',
  'customer_email',
  'customer_phone',
  'customer_notes',
  'bike_brand',
  'bike_model',
  'bike_year',
  'bike_type',
  'bike_size',
  'wheel_setup',
  'bike_serial',
  'component_type',
  'component_brand',
  'component_model',
  'component_serial',
  'component_setup_notes',
  'service_date',
  'service_title',
  'service_description',
  'amount_charged',
  'currency',
  'service_notes',
  'setup_text',
  'reminder_interval_months',
]

export const LEGACY_AI_PROVENANCE_COLUMNS = [
  'confidence',
  'warnings',
  'source_excerpt',
]

const COMPONENT_TYPES = new Set(['fork', 'shock', 'dropper', 'brake', 'wheel', 'other'])

export function legacyImportTemplateCsv() {
  const example = {
    source_system: 'Workshop spreadsheet',
    source_reference: 'ROW-001',
    customer_full_name: 'Matt Dormer',
    customer_first_name: '',
    customer_last_name: '',
    customer_email: 'matt@example.com',
    customer_phone: '07700 900000',
    customer_notes: 'Imported from old service log',
    bike_brand: 'Orange',
    bike_model: 'Stage 6',
    bike_year: '2020',
    bike_type: 'enduro',
    bike_size: 'L',
    wheel_setup: '29',
    bike_serial: '',
    component_type: 'fork',
    component_brand: 'Fox',
    component_model: '36 Float Performance',
    component_serial: '',
    component_setup_notes: 'Rider preferred firm compression',
    service_date: '2025-07-28',
    service_title: 'Full fork service',
    service_description: '125hr full fork service',
    amount_charged: '130.00',
    currency: 'GBP',
    service_notes: 'Dust wipers replaced',
    setup_text: '72 psi, 2 tokens',
    reminder_interval_months: '12',
  }
  return [
    LEGACY_IMPORT_COLUMNS.join(','),
    LEGACY_IMPORT_COLUMNS.map(column => csvCell(example[column])).join(','),
  ].join('\n')
}

export function parseLegacyCsv(text) {
  const table = parseCsv(text)
  if (table.length < 2) {
    return { rows: [], errors: ['CSV needs a header row and at least one data row.'] }
  }

  const headers = table[0].map(normaliseHeader)
  const named = headers.filter(Boolean)
  if (named.length !== new Set(named).size) {
    return { rows: [], errors: ['Duplicate CSV headings need review before local preview. AI Assist can map the original fields.'] }
  }
  const extra = table.slice(1).findIndex(values => values.slice(headers.length).some(value => cleanText(value)))
  if (extra >= 0) {
    return { rows: [], errors: [`Source row ${extra + 2} has data without a heading. AI Assist can map it, but local preview cannot ignore it.`] }
  }
  const missing = []
  const hasNamePath = headers.includes('customer_full_name')
    || (headers.includes('customer_first_name') && headers.includes('customer_last_name'))
  const hasServicePath = headers.includes('service_title') || headers.includes('service_description')
  if (!hasNamePath) missing.push('customer_full_name or customer_first_name/customer_last_name')
  if (!hasServicePath) missing.push('service_title or service_description')

  if (missing.length) {
    return { rows: [], errors: [`Missing columns: ${[...new Set(missing)].join(', ')}`] }
  }

  const rows = table.slice(1).map((values, index) => ({ values, rowNumber: index + 2 }))
    .filter(row => row.values.some(value => cleanText(value)))
    .map(({ values, rowNumber }) => {
      const raw = {}
      headers.forEach((header, columnIndex) => {
        if (header) raw[header] = cleanText(values[columnIndex])
      })
      return normaliseLegacyRow(raw, rowNumber)
    })

  return { rows, errors: [] }
}

export function legacyCsvTable(text) {
  const table = parseCsv(text)
  const data = table.slice(1).map((values, index) => ({ values, rowNumber: index + 2 }))
    .filter(row => row.values.some(value => cleanText(value)))
  return {
    headers: (table[0] || []).map(normaliseHeader),
    rawHeaders: table[0] || [],
    rows: data.map(row => row.values),
    rowNumbers: data.map(row => row.rowNumber),
  }
}

export function detectOldSystemExport(text) {
  const { headers } = legacyCsvTable(text)
  const names = new Set(headers)
  if ((names.has('id') || names.has('job_number'))
    && (names.has('created') || names.has('job_created') || names.has('scheduled'))
    && (names.has('status') || names.has('job_status') || names.has('invoice_status')
      || names.has('accepted_status') || names.has('job_accepted_status'))) {
    return 'historic_jobs'
  }
  if (names.has('first_name') && names.has('last_name')
    && (names.has('email') || names.has('mobile') || names.has('landline'))) {
    return 'customers'
  }
  return null
}

export async function readAiImportResponse(res) {
  if (!res.ok) {
    throw new Error(res.status === 504
      ? 'Analysis timed out. No import was made. Try a smaller, genuine service-history source later.'
      : 'Analysis failed. No import was made. Please try a genuine service-history source later.')
  }
  if (!res.headers.get('content-type')?.toLowerCase().includes('application/json')) {
    throw new Error('Analysis failed because the server did not return JSON. No import was made.')
  }
  let data
  try { data = await res.json() } catch { throw new Error('Analysis returned an invalid response. No import was made.') }
  if (!data || typeof data !== 'object' || data.error) throw new Error('Analysis failed. No import was made.')
  return data
}

export function legacyRowsToCsv(rows, { includeAiColumns = false } = {}) {
  const columns = includeAiColumns
    ? [...LEGACY_IMPORT_COLUMNS, ...LEGACY_AI_PROVENANCE_COLUMNS]
    : LEGACY_IMPORT_COLUMNS
  const body = (rows || []).map(row => columns.map(column => csvCell(serialiseCell(row?.[column]))).join(','))
  return [columns.join(','), ...body].join('\n')
}

export function normaliseAiProposedRows(rows, { maxRows = 80 } = {}) {
  if (!Array.isArray(rows)) {
    return { rows: [], errors: ['AI response did not include a rows array.'] }
  }
  if (rows.length > maxRows) {
    return { rows: [], errors: [`AI proposed ${rows.length} rows; split the source into ${maxRows} rows or fewer.`] }
  }

  return {
    rows: rows.map((row, index) => {
      const raw = {}
      LEGACY_IMPORT_COLUMNS.forEach(column => {
        raw[column] = cleanText(row?.[column])
      })
      raw.confidence = serialiseCell(row?.confidence)
      raw.warnings = serialiseCell(row?.warnings)
      raw.source_excerpt = cleanText(row?.source_excerpt).slice(0, 1000)
      return normaliseLegacyRow(raw, index + 1)
    }),
    errors: [],
  }
}

export function normaliseLegacyRow(raw, rowNumber = 0) {
  const firstLast = splitCustomerName(raw.customer_full_name, raw.customer_first_name, raw.customer_last_name)
  const serviceDate = normaliseDate(raw.service_date)
  const amount = normaliseMoney(raw.amount_charged)
  const interval = normalisePositiveInteger(raw.reminder_interval_months)
  const currency = cleanText(raw.currency || 'GBP').toUpperCase().slice(0, 3) || 'GBP'
  const aiConfidence = normaliseConfidence(raw.confidence || raw.ai_confidence)
  const aiWarnings = normaliseWarningList(raw.warnings || raw.ai_warnings)
  const aiSourceExcerpt = cleanText(raw.source_excerpt || raw.ai_source_excerpt).slice(0, 1000)
  const warnings = []
  const errors = []

  if (!firstLast.first_name) errors.push('Customer first name is required.')
  if (!firstLast.last_name && !cleanText(raw.customer_full_name).includes(' ')) {
    warnings.push('Customer has no last name; review before import.')
  }

  if (!cleanText(raw.service_title) && !cleanText(raw.service_description)) {
    errors.push('Service title or service description is required.')
  }
  if (cleanText(raw.service_date) && !serviceDate) warnings.push('Service date could not be parsed.')
  if (cleanText(raw.amount_charged) && amount === null) warnings.push('Amount charged could not be parsed.')
  if (cleanText(raw.reminder_interval_months) && !interval) warnings.push('Reminder interval must be a positive number of months.')

  const bike = {
    brand: cleanText(raw.bike_brand),
    model: cleanText(raw.bike_model),
    year: cleanText(raw.bike_year),
    bike_type: cleanText(raw.bike_type),
    bike_size: cleanText(raw.bike_size),
    wheel_setup: cleanText(raw.wheel_setup),
    serial: cleanText(raw.bike_serial),
  }
  const componentType = normaliseComponentType(raw.component_type)
  if (cleanText(raw.component_type) && componentType === 'other' && cleanText(raw.component_type).toLowerCase() !== 'other') {
    warnings.push('Component type is not recognised; review before creating a component.')
  }

  const component = {
    type: componentType,
    brand: cleanText(raw.component_brand),
    model: cleanText(raw.component_model),
    serial: cleanText(raw.component_serial),
    notes: cleanText(raw.component_setup_notes),
  }

  const hasBike = Boolean(bike.serial || bike.brand || bike.model || bike.year)
  const hasComponent = Boolean(component.serial || component.type || component.brand || component.model)
  const canCreateBike = Boolean(bike.brand && bike.model)
  const canCreateComponent = Boolean(component.type && component.brand && component.model)
  if (hasBike && !canCreateBike) {
    warnings.push('Bike data is incomplete; it will be linked only if clearly matched.')
  }
  if (hasComponent && !canCreateComponent) {
    warnings.push('Component data is incomplete; it will be linked only if clearly matched.')
  }
  if (aiConfidence !== null && aiConfidence < 0.75) {
    warnings.push('AI confidence is low; review before import.')
  }
  aiWarnings.forEach(message => {
    warnings.push(`AI: ${message}`)
  })

  const normalised = {
    row_number: rowNumber,
    source_system: cleanText(raw.source_system),
    source_reference: cleanText(raw.source_reference),
    customer: {
      first_name: firstLast.first_name,
      last_name: firstLast.last_name,
      email: legacyContact(raw.customer_email).toLowerCase(),
      phone: legacyContact(raw.customer_phone, true),
      notes: cleanText(raw.customer_notes),
    },
    bike,
    component,
    create_bike: canCreateBike,
    create_component: canCreateComponent,
    service: {
      service_date: serviceDate,
      service_date_text: cleanText(raw.service_date),
      service_title: cleanText(raw.service_title),
      service_description: cleanText(raw.service_description),
      amount_charged: amount,
      currency,
      notes: cleanText(raw.service_notes),
      setup_text: cleanText(raw.setup_text),
    },
    reminder_interval_months: interval,
    ai: {
      confidence: aiConfidence,
      warnings: aiWarnings,
      source_excerpt: aiSourceExcerpt,
    },
  }

  return {
    rowNumber,
    raw,
    normalised,
    warnings,
    errors,
    status: errors.length ? 'error' : warnings.length ? 'warning' : 'valid',
  }
}

export function legacyRowConfidence(row) {
  const value = row?.normalised?.ai?.confidence
  if (value !== null && value !== undefined) return value
  return row?.status === 'valid' ? 1 : 0.850
}

export function buildLegacySearchText(row, customer, bike, component) {
  return [
    row.source_system,
    row.source_reference,
    row.service_title,
    row.service_description,
    row.notes,
    row.setup_text,
    customer?.first_name,
    customer?.last_name,
    customer?.email,
    customer?.phone,
    bike?.brand,
    bike?.model,
    bike?.serial,
    component?.type,
    component?.brand,
    component?.model,
    component?.serial,
  ].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim()
}

export function suggestedOpportunity(serviceDate, intervalMonths) {
  if (!serviceDate || !intervalMonths) return null
  const due = addMonths(serviceDate, intervalMonths)
  if (!due) return null
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const dueDate = new Date(`${due}T00:00:00`)
  const overdue = dueDate < today
  return {
    opportunity_type: overdue ? 'overdue' : 'follow_up',
    suggested_due_date: due,
    reason: overdue
      ? `Imported service suggests a follow-up was due after ${intervalMonths} months.`
      : `Imported service suggests a follow-up in ${intervalMonths} months.`,
  }
}

function parseCsv(text) {
  const input = String(text || '').replace(/^\uFEFF/, '')
  const header = input.split(/\r?\n/, 1)[0]
  const delimiter = header.includes('\t') ? '\t' : ','
  const rows = []
  let row = []
  let cell = ''
  let inQuotes = false

  for (let i = 0; i < input.length; i++) {
    const char = input[i]
    const next = input[i + 1]

    if (char === '"' && inQuotes && next === '"') {
      cell += '"'
      i += 1
    } else if (char === '"') {
      inQuotes = !inQuotes
    } else if (char === delimiter && !inQuotes) {
      row.push(cell)
      cell = ''
    } else if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && next === '\n') i += 1
      row.push(cell)
      rows.push(row)
      row = []
      cell = ''
    } else {
      cell += char
    }
  }

  row.push(cell)
  if (row.some(value => cleanText(value)) || !input.endsWith('\n')) rows.push(row)
  return rows
}

function csvCell(value) {
  const text = String(value ?? '')
  if (!/[",\n\r]/.test(text)) return text
  return `"${text.replace(/"/g, '""')}"`
}

function serialiseCell(value) {
  if (Array.isArray(value)) return value.map(item => cleanText(item)).filter(Boolean).join(' | ')
  if (value && typeof value === 'object') return JSON.stringify(value)
  return cleanText(value)
}

function normaliseHeader(value) {
  return cleanText(value).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
}

function cleanText(value) {
  return String(value ?? '').replace(/\s+/g, ' ').trim()
}

function legacyContact(value, isPhone = false) {
  const text = cleanText(value)
  if (text === '-' || text === "'-") return ''
  return isPhone && /^'(?=\+?\d)/.test(text) ? text.slice(1) : text
}

function normaliseWarningList(value) {
  if (Array.isArray(value)) return value.map(cleanText).filter(Boolean).slice(0, 6)
  return cleanText(value)
    .split(/\s*(?:\||;)\s*/)
    .map(cleanText)
    .filter(Boolean)
    .slice(0, 6)
}

function normaliseConfidence(value) {
  const cleaned = cleanText(value)
  if (!cleaned) return null
  const number = Number(cleaned)
  if (!Number.isFinite(number)) return null
  if (number < 0) return 0
  if (number > 1) return 1
  return Number(number.toFixed(3))
}

function splitCustomerName(fullName, firstName, lastName) {
  const first = cleanText(firstName)
  const last = cleanText(lastName)
  if (first || last) return { first_name: first, last_name: last }

  const parts = cleanText(fullName).split(/\s+/).filter(Boolean)
  if (!parts.length) return { first_name: '', last_name: '' }
  if (parts.length === 1) return { first_name: parts[0], last_name: '' }
  return {
    first_name: parts.slice(0, -1).join(' '),
    last_name: parts[parts.length - 1],
  }
}

function normaliseMoney(value) {
  const cleaned = cleanText(value).replace(/[\u00a3$,]/g, '')
  if (!cleaned) return null
  const amount = Number(cleaned)
  return Number.isFinite(amount) && amount >= 0 ? Number(amount.toFixed(2)) : null
}

function normalisePositiveInteger(value) {
  const cleaned = cleanText(value)
  if (!cleaned) return null
  const number = Number(cleaned)
  return Number.isInteger(number) && number > 0 ? number : null
}

function normaliseComponentType(value) {
  const type = cleanText(value).toLowerCase()
  if (!type) return ''
  return COMPONENT_TYPES.has(type) ? type : 'other'
}

function normaliseDate(value) {
  const text = cleanText(value)
  if (!text) return null
  if (/^\d{4}-\d{2}-\d{2}$/.test(text) && isValidDate(text)) return text

  const slashMatch = text.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2}|\d{4})$/)
  if (slashMatch) {
    const day = slashMatch[1].padStart(2, '0')
    const month = slashMatch[2].padStart(2, '0')
    const rawYear = slashMatch[3]
    const year = rawYear.length === 2 ? `20${rawYear}` : rawYear
    const iso = `${year}-${month}-${day}`
    if (isValidDate(iso)) return iso
  }

  const parsed = new Date(text)
  if (!Number.isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10)
  return null
}

function isValidDate(isoDate) {
  const match = String(isoDate || '').match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return false
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const date = new Date(Date.UTC(year, month - 1, day))
  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day
}

function addMonths(isoDate, months) {
  if (!isValidDate(isoDate)) return null
  const [year, month, day] = isoDate.split('-').map(Number)
  const date = new Date(Date.UTC(year, month - 1, day))
  date.setUTCMonth(date.getUTCMonth() + months)
  if (date.getUTCDate() !== day) date.setUTCDate(0)
  return date.toISOString().slice(0, 10)
}
